<template>
  <BaseModal
    class="scoped"
    :container-class="MODAL_CONTAINER_WIDTH_CLASSES"
    content-class="tw:overflow-y-auto"
    form
    header-class="tw:p-4"
    :modal-style="{ maxWidth: '50vw', maxHeight: '90vh' }"
    :title="getLocaleText('external_item_manual_add_modal.title')"
    @close="modalClosed"
    @submit="save"
  >
    <template #header>
      <div class="tw:flex tw:items-center tw:gap-2">
        <BackButton @click="handleGoBack" />

        <div class="tw:text-lg tw:font-bold tw:flex-1 tw:text-center">
          {{ getLocaleText("external_item_manual_add_modal.title") }}
        </div>
      </div>
    </template>

    <div class="tw:px-10 tw:py-6 tw:max-w-175 tw:mx-auto">
      <ExternalItemForm
        v-if="!loading"
        v-model:accept-conditions="acceptConditions"
        v-model:selected-stream-ids="selectedStreamIds"
        data-testid="external-item-form"
        :disabled-fields="openedFromBulkAdd ? ['streams'] : []"
        :external-item="externalItem!"
      />
    </div>

    <template #buttons>
      <BaseButton
        color="secondary"
        data-testid="cancel-button"
        :disabled="savingInProgress"
        :label="getLocaleText('global.cancel')"
        new-design
        @click="handleCancel"
      />
      <BaseButton
        color="primary"
        data-testid="save-button"
        :disabled="savingInProgress"
        :label="getLocaleText('global.save')"
        :loading="savingInProgress"
        new-design
        type="submit"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { onMounted } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton } from "shared/components/base";
import BaseModal from "shared/components/core/modals/BaseModal.vue";
import ExternalItemForm from "shared/components/ExternalItemForm.vue";
import useExternalItemForm from "shared/composables/useExternalItemForm";
import { type ExternalItem } from "shared/resources";

import BackButton from "./shared/BackButton.vue";
import { MODAL_CONTAINER_WIDTH_CLASSES } from "./shared/constants";

// Define component name for registration
defineOptions({
  name: "ExternalItemManualAddModal",
});

export interface ExternalItemManualAddModalProps {
  openedFromBulkAdd?: boolean;
  preselectedUrl?: string;
  savingInProgress?: boolean;
  streamId: number;
}

const props = withDefaults(defineProps<ExternalItemManualAddModalProps>(), {
  preselectedUrl: undefined,
});

const emit = defineEmits<{
  addExternalItem: [];
  cancel: [];
  goBack: [];
  modalClosed: [];
  save: [ExternalItem];
}>();

const {
  externalItem,
  acceptConditions,
  selectedStreamIds,
  loadExternalItem,
  loading,

  confirmCancelEditExternalItem,
  prepareToSave,
} = useExternalItemForm({
  prefilledUrl: props.preselectedUrl,
  streamId: props.streamId,
});

onMounted(loadExternalItem);

const handleGoBack = () => {
  confirmCancelEditExternalItem(() => emit("goBack"));
};

const handleCancel = () => {
  confirmCancelEditExternalItem(() => emit("cancel"));
};

function modalClosed() {
  emit("modalClosed");
}

async function save() {
  const canSave = await prepareToSave();
  if (!canSave) return;

  emit("save", externalItem.value!);
}
</script>
